import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  Divider,
  Chip,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  ADMIN_LISTING_FAQS_QUERY,
  ADMIN_UPSERT_LISTING_FAQ_MUTATION,
  ADMIN_DELETE_LISTING_FAQ_MUTATION,
} from "../../graphql/faqs";

const PRODUCT_TYPES = [
  { value: "ring", label: "Rings" },
  { value: "necklace", label: "Necklaces & Pendants" },
  { value: "ear rings", label: "Earrings" },
  { value: "bracelets", label: "Bracelets" },
  { value: "karas", label: "Karas" },
  { value: "bridal sets", label: "Bridal Sets" },
  { value: "wedding band", label: "Wedding Bands" },
];

const METALS = [
  { value: "", label: "All Metals (no filter)" },
  { value: "gold", label: "Gold" },
  { value: "diamond", label: "Diamond" },
  { value: "diamond polki", label: "Diamond Polki" },
  { value: "platinum", label: "Platinum" },
  { value: "palladium", label: "Palladium" },
];

type FaqRow = {
  id: string;
  question: string;
  answer: string;
  productType: string;
  metal?: string | null;
  position: number;
  active: boolean;
};

type FaqForm = {
  id?: string;
  productType: string;
  metal: string;
  question: string;
  answer: string;
  position: number;
  active: boolean;
};

const emptyForm: FaqForm = {
  productType: "ring",
  metal: "",
  question: "",
  answer: "",
  position: 0,
  active: true,
};

export default function ListingFaqsAdminPage() {
  const { data, loading, error, refetch } = useQuery(ADMIN_LISTING_FAQS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [upsert, upsertState] = useMutation(ADMIN_UPSERT_LISTING_FAQ_MUTATION);
  const [deleteFaq, deleteState] = useMutation(
    ADMIN_DELETE_LISTING_FAQ_MUTATION,
  );

  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<FaqRow | null>(null);
  const [form, setForm] = useState<FaqForm>(emptyForm);

  const faqs: FaqRow[] = data?.adminListingFaqs ?? [];

  const openCreate = () => {
    setForm(emptyForm);
    setOpenForm(true);
  };
  const openEdit = (r: FaqRow) => {
    setForm({
      id: r.id,
      productType: r.productType,
      metal: r.metal ?? "",
      question: r.question,
      answer: r.answer,
      position: r.position,
      active: r.active,
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    const res = await upsert({
      variables: {
        id: form.id || null,
        productType: form.productType,
        metal: form.metal || null,
        question: form.question.trim(),
        answer: form.answer.trim(),
        position: form.position,
        active: form.active,
      },
    });
    const errors = res?.data?.adminUpsertListingFaq?.errors ?? [];
    if (errors.length) return;
    setOpenForm(false);
    await refetch();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteFaq({ variables: { id: confirmDelete.id } });
    setConfirmDelete(null);
    await refetch();
  };

  const getPageLabel = (r: FaqRow) => {
    const type =
      PRODUCT_TYPES.find((t) => t.value === r.productType)?.label ??
      r.productType;
    const metal = r.metal
      ? ` — ${r.metal.charAt(0).toUpperCase() + r.metal.slice(1)}`
      : " — All Metals";
    return `${type}${metal}`;
  };

  const formErrors: string[] =
    upsertState.data?.adminUpsertListingFaq?.errors ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={2}
        spacing={2}
      >
        <Box>
          <Typography variant="h5">Products Listing FAQs</Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            FAQs for category pages. Select product type + metal to target a
            specific page. e.g. "Rings + Gold" shows only on the Gold Rings
            page. "Rings + All Metals" shows on the All Rings page. Max 3 FAQs
            per page combination recommended.
          </Alert>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add FAQ
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 200 }}>Page</TableCell>
                <TableCell sx={{ width: 60 }}>Order</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Question</TableCell>
                <TableCell>Answer</TableCell>
                <TableCell sx={{ width: 80 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faqs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No listing FAQs added yet.
                  </TableCell>
                </TableRow>
              )}
              {faqs.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getPageLabel(r)}
                      variant="outlined"
                      sx={{ borderRadius: 1, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>{r.position}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                    {r.question}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        fontSize: 13,
                        color: "text.secondary",
                      }}
                    >
                      {r.answer}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.active ? "Active" : "Hidden"}
                      color={r.active ? "success" : "default"}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(r)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* FORM DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            {form.id ? "Edit Listing FAQ" : "Add Listing FAQ"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          {formErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.join(" • ")}
            </Alert>
          )}
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Select the page this FAQ belongs to. "All Metals" means it shows
              on the category page without a metal filter (e.g. All Rings).
              Selecting a metal means it shows only on that specific page (e.g.
              Gold Rings).
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Product Type"
                value={form.productType}
                onChange={(e) =>
                  setForm((p) => ({ ...p, productType: e.target.value }))
                }
                fullWidth
              >
                {PRODUCT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Metal Filter"
                value={form.metal}
                onChange={(e) =>
                  setForm((p) => ({ ...p, metal: e.target.value }))
                }
                fullWidth
              >
                {METALS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Question"
              value={form.question}
              onChange={(e) =>
                setForm((p) => ({ ...p, question: e.target.value }))
              }
              fullWidth
              placeholder="e.g. What is the difference between 18K and 22K gold?"
            />
            <TextField
              label="Answer"
              value={form.answer}
              onChange={(e) =>
                setForm((p) => ({ ...p, answer: e.target.value }))
              }
              fullWidth
              multiline
              minRows={4}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Display Order"
                type="number"
                value={form.position}
                onChange={(e) =>
                  setForm((p) => ({ ...p, position: Number(e.target.value) }))
                }
                fullWidth
                helperText="Lower = shown first"
              />
              <TextField
                select
                label="Status"
                value={form.active ? "active" : "hidden"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    active: e.target.value === "active",
                  }))
                }
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="hidden">Hidden</MenuItem>
              </TextField>
            </Stack>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setOpenForm(false)}
              disabled={upsertState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                upsertState.loading ||
                !form.question.trim() ||
                !form.answer.trim()
              }
            >
              {upsertState.loading ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Delete FAQ?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Delete: <b>"{confirmDelete?.question}"</b>
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteState.loading}
            >
              {deleteState.loading ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
